/**
 * LiveClassDetailsDrawer — side sheet showing full class details + host actions.
 *
 * Renders when a class chip or row is clicked on the Calendar tab.
 * Non-disruptive (slides in from the right, doesn't block the calendar behind).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { Radio, Calendar, Clock, Users, Video, Copy, ExternalLink, Info, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const fmtIST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'full', timeStyle: 'short' });
  } catch { return iso; }
};

const statusStyle = (s) => {
  switch (s) {
    case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'live': return 'bg-red-500 text-white animate-pulse border-red-600';
    case 'completed': return 'bg-gray-200 text-gray-700 border-gray-300';
    case 'cancelled': return 'bg-red-100 text-red-700 border-red-300';
    default: return 'bg-gray-200 text-gray-700';
  }
};

const canStartAsHost = (cls, user) => {
  if (!user || !cls) return false;
  const isAssignedHost = cls.host_user_id === user.id;
  const isAdmin = user.role === 'admin';
  if (!isAssignedHost && !isAdmin) return false;
  if (cls.status === 'cancelled' || cls.status === 'completed') return false;
  const now = Date.now();
  const start = new Date(cls.start_time).getTime();
  const end = start + (cls.duration_minutes + 30) * 60 * 1000;
  const openAt = start - 15 * 60 * 1000;
  return now >= openAt && now <= end;
};

const LiveClassDetailsDrawer = ({ open, onOpenChange, cls }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!cls) return null;

  const startable = canStartAsHost(cls, user);
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success('Copied'));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="live-class-details-drawer">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${statusStyle(cls.status)} border`}>
              {cls.status === 'live' && <Radio className="w-3 h-3 mr-1" />}
              {cls.status.toUpperCase()}
            </Badge>
            {Array.isArray(cls.grades) && cls.grades.length > 0 && cls.grades.map(g => (
              <Badge key={g} variant="outline">{g}</Badge>
            ))}
          </div>
          <SheetTitle className="text-xl">{cls.title}</SheetTitle>
          {cls.description && (
            <SheetDescription className="mt-1">{cls.description}</SheetDescription>
          )}
        </SheetHeader>

        {/* Start CTA */}
        {startable && (
          <div className="my-4">
            <Button onClick={() => navigate(`/admin/live-classes/${cls.id}/host`)}
              className="w-full bg-red-600 hover:bg-red-700 text-white h-11"
              data-testid="drawer-start-class-btn">
              <Radio className="w-4 h-4 mr-2" />
              Start Class (as Host)
            </Button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              You&apos;ll join as the host of this Zoom meeting.
            </p>
          </div>
        )}

        {/* Details */}
        <div className="mt-6 space-y-4 text-sm">
          <div className="flex items-start gap-3">
            <Calendar className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Start (IST)</p>
              <p className="font-medium text-gray-900">{fmtIST(cls.start_time)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="font-medium text-gray-900">{cls.duration_minutes} minutes</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Users className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Host</p>
              <p className="font-medium text-gray-900">{cls.host_user_name}</p>
              {cls.host_user_email && <p className="text-xs text-gray-500">{cls.host_user_email}</p>}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <GraduationCap className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Grade</p>
              <p className="font-medium text-gray-900">
                {Array.isArray(cls.grades) && cls.grades.length > 0
                  ? cls.grades.join(', ')
                  : <span className="text-gray-500 font-normal">No grade set</span>}
              </p>
            </div>
          </div>

          {cls.zoom_account_label && (
            <div className="flex items-start gap-3">
              <Video className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Zoom License</p>
                <p className="font-medium text-gray-900">{cls.zoom_account_label}</p>
              </div>
            </div>
          )}

          {cls.zoom_meeting_id && (
            <div className="p-3 bg-gray-50 border rounded-md space-y-2">
              <p className="text-xs font-semibold text-gray-700">Zoom Meeting</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-20">Meeting ID</span>
                <span className="font-mono text-sm flex-1">{cls.zoom_meeting_id}</span>
                <Button size="sm" variant="ghost" onClick={() => copy(String(cls.zoom_meeting_id))} className="h-6 w-6 p-0">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              {cls.zoom_meeting_password && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-20">Password</span>
                  <span className="font-mono text-sm flex-1">{cls.zoom_meeting_password}</span>
                  <Button size="sm" variant="ghost" onClick={() => copy(cls.zoom_meeting_password)} className="h-6 w-6 p-0">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              )}
              {cls.zoom_join_url && (
                <a href={cls.zoom_join_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#0A7EA4] hover:underline mt-1">
                  Open in Zoom native app <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {!startable && cls.status === 'scheduled' && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex gap-2 text-xs text-blue-900">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{cls.host_user_id === user?.id
                ? 'Start button appears 15 min before class begins.'
                : 'Only the assigned host or a full admin can start this class.'}</span>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LiveClassDetailsDrawer;
